import { useCallback, useState } from 'react'
import { Modal, Button } from 'antd'
import useLogin from '@/hooks/swr/useLogin'
import { track } from '@/utils/track'

// 通用「付费功能守卫」：点击付费动作时统一处理——
// 1) 埋点 paid_feature（点击/点数不足/成交/去充值/去开通VIP）；
// 2) VIP(vip_level>=20)免费；点数充足→二次确认；点数不足→引导充值/开通VIP。
// 用法：
//   const { guard, tokenGuardModal, isVip } = useTokenGuard()
//   <Button onClick={() => guard({ feature: 'match_document', cost: 1000, label: '生成赛事文档', onConfirm: doGenerate })}>...</Button>
//   {tokenGuardModal}
type GuardOpts = { feature: string; cost: number; label?: string; onConfirm: () => void }

export default function useTokenGuard() {
  const { current_user, vipLevel } = useLogin()
  const isVip = (vipLevel ?? 0) >= 20
  const balance = current_user?.lingti_tokens ?? 0

  const [opts, setOpts] = useState<GuardOpts | null>(null)
  const [mode, setMode] = useState<'confirm' | 'insufficient'>('confirm')

  const guard = useCallback(
    (o: GuardOpts) => {
      track('paid_feature', { target: `${o.feature}:click`, vip: isVip, balance })
      if (!isVip && balance < o.cost) {
        track('paid_feature', { target: `${o.feature}:insufficient`, balance })
        setMode('insufficient')
      } else {
        setMode('confirm')
      }
      setOpts(o)
    },
    [isVip, balance]
  )

  const close = () => setOpts(null)

  const doConfirm = () => {
    if (!opts) return
    track('paid_feature', { target: `${opts.feature}:generated`, value: isVip ? 0 : opts.cost })
    opts.onConfirm()
    close()
  }

  const goRecharge = () => {
    if (opts) track('paid_feature', { target: `${opts.feature}:recharge_click` })
    window.open('/billing')
  }
  const goVip = () => {
    if (opts) track('paid_feature', { target: `${opts.feature}:vip_click` })
    window.open('/vip_billing')
  }

  const tokenGuardModal = opts ? (
    mode === 'confirm' ? (
      <Modal open title={`确认${opts.label || '使用'}`} onCancel={close} onOk={doConfirm} okText="确认" cancelText="取消">
        <p className="text-base">
          {isVip ? `VIP 免费，确认${opts.label || '生成'}？` : <>将消耗 <b>{opts.cost}</b> 灵缇点数（当前 <b>{balance}</b> 点），确认后不可撤销。</>}
        </p>
      </Modal>
    ) : (
      <Modal
        open
        title="灵缇点数不足"
        onCancel={close}
        footer={[
          <Button key="cancel" onClick={close}>
            取消
          </Button>,
          <Button key="vip" onClick={goVip}>
            开通 VIP（免费）
          </Button>,
          <Button key="recharge" type="primary" onClick={goRecharge}>
            充值点数
          </Button>,
        ]}
      >
        <p className="text-base">
          {opts.label || '该功能'}需 <b>{opts.cost}</b> 点，你当前 <b>{balance}</b> 点。
        </p>
        <p className="text-gray-500">充值点数后即可使用；或开通 VIP，之后使用相关付费功能免费。</p>
      </Modal>
    )
  ) : null

  return { guard, tokenGuardModal, isVip, balance }
}
